import { useTranslation } from 'react-i18next';
import React, { useState } from "react";

import ConfirmationModal from "../ConfirmationModal";
import type { OptionItem, OptionsInterface } from "./OptionsInterface";
import { useToast } from '../Toast.hook';

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Button from '@mui/material/Button';

import CancelIcon from '@mui/icons-material/Cancel';

function OptionsModal({ open, onClose, options, label }: {
    open: boolean;
    onClose: () => void;
    options: OptionsInterface[];
    label: string;
}) {
    const { t } = useTranslation();
    const [triggerMessage] = useToast();

    const [selectedOption, setSelectedOption] = useState<OptionItem | null>(null);
    const [confirmationModal, setConfirmationModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleOptionClick = (option: OptionItem) => {
        if (option.endpoint === 'STATUS') {
            onClose();
            option.onClick?.();
            return;
        }

        if (option.confirmationRequired) {
            setSelectedOption(option);
            setConfirmationModal(true);
        } else {
            executeOption(option);
        }
    };

    const executeOption = async (option: OptionItem) => {
        try {
            setIsLoading(true);
            await handleOptions(option.endpoint);
            onClose();
        } finally {
            setIsLoading(false);
            setSelectedOption(null);
            setConfirmationModal(false);
        }
    };

    const handleOptions = async (url: string) => {
        try {
            const token = localStorage.getItem('userToken');

            const res = await fetch(url, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });

            // const ct = res.headers.get('content-type') || '';
            // const body = ct.includes('application/json') ? await res.json() : await res.text();

            if (res.ok) {
                //triggerMessage(body?.message || body || 'Operation completed successfully.', false);
                triggerMessage(t('mdm.operation_successful'), false);
                return;
            }

            if (res.status === 401) {
                //triggerMessage(body?.error || body?.message || body || 'Not authorized — please log in.', true);
                triggerMessage(t('mdm.not_authorized'), true);
                return;
            }

            //triggerMessage('Error: ' + (body?.error || body?.message || body || `Status ${res.status}`), true);
            triggerMessage(`${t('mdm.operation_failed')} ${res.status}`, true);
        } catch (err: any) {
            //triggerMessage('Unexpected error: ' + (err.message || String(err)), true);
            triggerMessage(t('mdm.unexpected_error'), true);
        }
    };

    return (
        <Dialog open={open} onClose={isLoading ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {label || t("mdm.options")}
            </DialogTitle>

            <DialogContent dividers sx={{ position: "relative" }}>
                {isLoading && (
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            backgroundColor: "rgba(255,255,255,0.8)",
                            zIndex: 10,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 2
                        }}
                    >
                        <CircularProgress size={40} />
                        <Typography variant="body2" color="text.secondary">
                            {t("mdm.processing")}...
                        </Typography>
                    </Box>
                )}

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {options.map((option, index) => {
                        if (option.type === "divider") {
                            return (
                                <Box
                                    key={`divider-${index}`}
                                    sx={{
                                        my: 1,
                                        borderBottom: "1px solid",
                                        borderColor: "divider"
                                    }}
                                />
                            );
                        }

                        return (
                            <Box
                                key={option.endpoint}
                                onClick={() => !isLoading && handleOptionClick(option)}
                                sx={{
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 1,
                                    p: 2,
                                    cursor: isLoading ? "not-allowed" : "pointer",
                                    transition: "all 0.2s",
                                    backgroundColor: "background.paper",
                                    opacity: isLoading ? 0.5 : 1,
                                    "&:hover": isLoading
                                        ? {}
                                        : {
                                            borderColor: `${option.style}.main`,
                                            boxShadow: 1
                                        }
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                                    <Box sx={{ color: `${option.style}.main` }}>
                                        {option.icon}
                                    </Box>

                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            variant="subtitle1"
                                            sx={{
                                                fontWeight: 600,
                                                mb: 0.5,
                                                color: "text.primary"
                                            }}
                                        >
                                            {option.label}
                                        </Typography>

                                        {option.description && (
                                            <Typography variant="body2" color="text.secondary">
                                                {option.description}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </DialogContent>

            <DialogActions>
                <Button
                    variant="contained"
                    color="error"
                    size="small"
                    onClick={onClose}
                    startIcon={<CancelIcon fontSize="small" />}
                    disabled={isLoading}
                    sx={{
                        textTransform: "none",
                        fontSize: "0.75rem",
                        padding: "6px 10px",
                        minHeight: "30px"
                    }}
                >
                    {t("mdm.cancel")}
                </Button>
            </DialogActions>

            <ConfirmationModal
                open={confirmationModal}
                onConfirm={() => selectedOption && executeOption(selectedOption)}
                onClose={() => setConfirmationModal(false)}
            />
        </Dialog>
    );
}

export default OptionsModal;